/**
 * Recorded classes. Students watch; trainers manage (publish, hide, chapters). Both use the same player.
 *
 * Playback uses a native <video controls> element, so keyboard, captions and picture-in-picture work for free.
 * The <video> lives inside a data-morph="skip" host: this screen never re-renders the element that is playing.
 */
import { api } from '../../core/api.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { mmss } from '../../core/format.js';
import { currentView, every, reload, update } from '../../app/shell.js';
import { backLink, banner, bar, empty, ic, statusTag, table, tag } from '../../ui/components.js';
import { field } from '../../ui/forms.js';
import { closeModal, openModal } from '../../ui/modal.js';
import { submitForm } from '../../ui/submit.js';
import { toast } from '../../ui/toast.js';

const SAVE_EVERY_MS = 15000;
let videoEl = null;
let lastSaved = -1;
let recordingId = null;
let canTrack = false;

/* ---------- list ---------- */

function row(r, area) {
  return html`<div class="li rec"><span class="recn">${r.number}</span><span class="f1"><b>${r.title}</b><br><span class="xs mu">${mmss(r.duration_s)}${r.days_left != null ? ` · ${r.days_left} days left` : ''}</span></span>
    ${r.watched_percent >= 100 ? tag('Watched', 'ok') : r.watched_percent > 0 ? tag(`${r.watched_percent}%`, 'wn') : ''}
    <a class="btn soft sm" href="/${area}/recordings/${r.id}">${r.watched_percent > 0 && r.watched_percent < 100 ? 'Resume' : 'Watch'}</a></div>`;
}

const studentList = {
  path: '/student/recordings',
  roles: ['student'],
  title: 'Recordings',
  ui: () => ({ q: '' }),
  load: (ctx) => api.get('/recordings', { signal: ctx.signal }),
  view(d, ui) {
    const q = ui.q.trim().toLowerCase();
    const byCourse = new Map();
    for (const r of d.recordings) {
      if (!byCourse.has(r.course)) byCourse.set(r.course, []);
      byCourse.get(r.course).push(r);
    }
    const resume = d.recordings.filter((r) => r.watched_percent > 0 && r.watched_percent < 100).sort((a, b) => b.number - a.number)[0];
    return html`<div class="page-h"><div><h2 style="margin:0 0 4px">Recorded classes</h2><p>Every live class is recorded. Watch again while your access is active.</p></div>
      <input class="inline-in" style="width:240px" placeholder="Search classes" aria-label="Search classes" value="${ui.q}" data-input="rec.search"></div>
      ${resume && !q ? html`<div class="card tint cont"><div><b>Pick up where you left off</b><div class="sm mu">${resume.course}, class ${resume.number}: ${resume.title} (${resume.watched_percent}% watched)</div></div><a class="btn" href="/student/recordings/${resume.id}">${ic('tri')} Resume</a></div>` : ''}
      ${d.recordings.length
        ? [...byCourse].map(([course, list]) => {
          const shown = list.filter((r) => !q || r.title.toLowerCase().includes(q));
          const done = list.filter((r) => r.watched_percent >= 100).length;
          return html`<section class="mt"><div class="row between"><div><h3 style="margin:0">${course}</h3><p class="sm mu" style="margin:2px 0 0">${done} of ${list.length} watched</p></div><div style="width:160px">${bar(Math.round((done / list.length) * 100), 'ok', 'Watched')}</div></div>
            <div class="card flat mt-s" style="padding:0 18px"><div class="list">${shown.length ? shown.map((r) => row(r, 'student')) : html`<p class="mu sm" style="padding:14px 0;margin:0">No class matches that search.</p>`}</div></div></section>`;
        })
        : empty('cam', 'No recordings yet', 'Recordings appear here a little while after each class ends.', html`<a class="btn" href="/student/classes">See live classes</a>`)}`;
  },
};

register({ 'rec.search': (el) => update((ui) => { ui.q = el.value; }) });

/* ---------- trainer: manage ---------- */

const trainerList = {
  path: '/professional/recordings',
  roles: ['professional'],
  nav: 'coaching',
  title: 'Class recordings',
  load: (ctx) => api.get('/recordings', { signal: ctx.signal }),
  view(d) {
    return html`${backLink('/professional/coaching', 'Coaching')}<div class="page-h"><div><h2 style="margin:0 0 4px">Class recordings</h2><p>Every class you teach is recorded. Hidden recordings disappear from students' lists.</p></div></div>
      ${table(['Class', 'Length', 'Views', 'Status', ''], d.recordings.map((r) => [
        html`<b>Class ${r.number}</b> ${r.title}<div class="xs mu">${r.course}</div>`, mmss(r.duration_s), r.views,
        r.status !== 'ready' ? statusTag(r.status) : r.published ? tag('Published', 'ok') : tag('Hidden', 'n'),
        html`<div class="row" style="flex-wrap:nowrap">${r.status === 'ready' ? html`<a class="btn ghost sm" href="/professional/recordings/${r.id}">Open</a>
          <button type="button" class="btn ghost sm" data-act="rec.chapters" data-id="${r.id}">Chapters</button>
          <button type="button" class="btn soft sm" data-act="rec.publish" data-id="${r.id}" data-on="${!r.published}">${r.published ? 'Hide' : 'Publish'}</button>` : ''}</div>`,
      ]), { empty: 'Recordings of your classes appear here.' })}`;
  },
};

register({
  'rec.publish': async (el) => {
    const on = el.dataset.on === 'true';
    await api.post(`/recordings/${el.dataset.id}/publish`, { body: { published: on } });
    toast(on ? 'Recording published' : 'Recording hidden from students', 'eye');
    await reload();
  },
  'rec.chapters': async (el) => {
    const { recording } = await api.get(`/recordings/${el.dataset.id}`);
    const text = (recording.chapters || []).map((c) => `${mmss(c.start_s)} ${c.title}`).join('\n');
    openModal({
      title: 'Chapters',
      body: html`<p class="mu sm">One chapter per line: the time, then the title. Example: <span class="mono">12:40 Postman basics</span></p>
        <form id="chapter-form" data-submit="rec.chapters.save" data-id="${recording.id}" novalidate>${field('ch-text', 'Chapters', { name: 'chapters', rows: 8, val: text, max: 4000, err: 'Add at least one chapter.' })}</form>`,
      foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="chapter-form" class="btn">Save chapters</button>`,
    });
  },
  'rec.chapters.save': (form) => submitForm(form, async ({ chapters }) => {
    const parsed = [];
    for (const line of chapters.split('\n').map((l) => l.trim()).filter(Boolean)) {
      const m = /^(?:(\d+):)?(\d{1,2}):(\d{2})\s+(.+)$/.exec(line);
      if (!m) throw new Error(`Could not read "${line}". Use the form 12:40 Title.`);
      parsed.push({ start_s: Number(m[1] || 0) * 3600 + Number(m[2]) * 60 + Number(m[3]), title: m[4].slice(0, 100) });
    }
    await api.put(`/recordings/${form.dataset.id}/chapters`, { body: { chapters: parsed } });
    closeModal(false);
    toast('Chapters saved');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),
});

/* ---------- player ---------- */

async function saveProgress(force = false) {
  if (!canTrack || !videoEl || recordingId == null) return;
  const position = Math.floor(videoEl.currentTime || 0);
  if (!force && Math.abs(position - lastSaved) < 5) return;
  lastSaved = position;
  try {
    await api.post(`/recordings/${recordingId}/progress`, { body: { position_s: position } });
  } catch {
    /* progress is a convenience; never interrupt playback */
  }
}

function mountVideo(host, rec, area) {
  videoEl = document.createElement('video');
  videoEl.className = 'player';
  videoEl.controls = true;
  videoEl.preload = 'metadata';
  videoEl.setAttribute('playsinline', '');
  videoEl.setAttribute('controlslist', 'nodownload');
  videoEl.setAttribute('aria-label', rec.title);
  videoEl.src = rec.stream_url;
  recordingId = rec.id;
  canTrack = area === 'student';
  lastSaved = -1;
  const start = rec.resume_s || 0;
  videoEl.addEventListener('loadedmetadata', () => {
    if (start > 0 && start < videoEl.duration - 5) videoEl.currentTime = start;
  }, { once: true });
  videoEl.addEventListener('pause', () => saveProgress(true));
  videoEl.addEventListener('ended', () => saveProgress(true));
  videoEl.addEventListener('timeupdate', () => {
    const view = currentView();
    const chapters = rec.chapters || [];
    const index = chapters.reduce((acc, c, i) => (videoEl.currentTime >= c.start_s ? i : acc), 0);
    if (view?.ui && view.ui.chapter !== index) update((ui) => { ui.chapter = index; });
  });
  videoEl.addEventListener('error', async () => {
    // Signed links expire. Ask for a fresh one once and carry on from the same spot.
    const at = videoEl.currentTime;
    try {
      const fresh = await api.get(`/recordings/${rec.id}`);
      videoEl.src = fresh.recording.stream_url;
      videoEl.currentTime = at;
      videoEl.play().catch(() => {});
    } catch {
      toast('This recording could not be played. Try again in a moment.', 'alert');
    }
  }, { once: true });
  host.replaceChildren(videoEl);
}

function player(area) {
  return {
    path: `/${area}/recordings/:id`,
    roles: [area],
    nav: area === 'student' ? 'recordings' : 'coaching',
    title: (d) => d?.recording?.title || 'Recording',
    ui: () => ({ chapter: 0, speed: 1, saved: true }),
    async load(ctx) {
      const [one, all] = await Promise.all([
        api.get(`/recordings/${ctx.params.id}`, { signal: ctx.signal }),
        api.get('/recordings', { signal: ctx.signal }).catch(() => ({ recordings: [] })),
      ]);
      const same = all.recordings.filter((r) => r.course_id === one.recording.course_id && r.batch_id === one.recording.batch_id).sort((a, b) => a.number - b.number);
      const i = same.findIndex((r) => r.id === one.recording.id);
      return { recording: one.recording, prev: same[i - 1] || null, next: same[i + 1] || null };
    },
    enter() {
      const d = currentView().data;
      const host = document.getElementById(`player-${d.recording.id}`);
      if (host && d.recording.stream_url) mountVideo(host, d.recording, area);
      every(SAVE_EVERY_MS, () => (videoEl && !videoEl.paused ? saveProgress() : undefined));
    },
    leave() {
      saveProgress(true);
      videoEl?.pause();
      videoEl?.removeAttribute('src');
      videoEl?.load();
      videoEl = null;
      recordingId = null;
    },
    view(d, ui) {
      const r = d.recording;
      const chapters = r.chapters || [];
      return html`${backLink(`/${area}/recordings`, 'Recordings')}
        <div class="row between mb"><div><h2 style="margin:0">${r.title}</h2><p class="mu sm" style="margin:4px 0 0">${r.course}, class ${r.number}</p></div>
          ${r.days_left != null ? tag(`Available for ${r.days_left} more days`, 'n') : ''}</div>
        <div class="room"><div>
          <div class="stage" style="padding:0;overflow:hidden">${r.stream_url ? html`<div id="player-${r.id}" data-key="player-${r.id}" data-morph="skip"></div>` : banner('This recording is still processing. We will notify you when it is ready.', '', 'info')}</div>
          <div class="pctl" style="color:inherit"><label class="sm" for="speed">Speed</label>
            <select id="speed" aria-label="Playback speed" data-change="rec.speed">${[0.75, 1, 1.25, 1.5, 2].map((s) => html`<option value="${s}" ${s === ui.speed ? 'selected' : ''}>${s}x</option>`)}</select></div>
          <div class="row mt-s">${d.prev ? html`<a class="btn ghost" href="/${area}/recordings/${d.prev.id}">${ic('left')} Previous class</a>` : ''}${d.next ? html`<a class="btn ghost" href="/${area}/recordings/${d.next.id}">Next class</a>` : ''}</div></div>
          <div class="col">${chapters.length ? html`<div class="card" style="padding:10px"><h4 style="padding:6px 12px 2px;margin:0">Chapters</h4>
            ${chapters.map((c, i) => html`<button type="button" class="chap ${i === ui.chapter ? 'on' : ''}" data-act="rec.jump" data-start="${c.start_s}"><span class="ts">${mmss(c.start_s)}</span><span>${c.title}</span></button>`)}</div>` : ''}
            ${area === 'student' ? html`<div class="card"><div class="row between"><h4 style="margin:0">My notes</h4><button type="button" class="btn ghost sm" data-act="rec.stamp">Add timestamp</button></div>
              <textarea id="rec-note" rows="5" class="mt-s" placeholder="Write what you want to remember" data-change="rec.note" data-id="${r.id}">${r.note || ''}</textarea></div>` : ''}</div></div>
        <p class="xs mu mt-s">Recordings are for enrolled students only. Please do not share them.</p>`;
    },
  };
}

register({
  'rec.speed': (el) => {
    if (videoEl) videoEl.playbackRate = Number(el.value);
    currentView().ui.speed = Number(el.value);
  },
  'rec.jump': (el) => {
    if (!videoEl) return;
    videoEl.currentTime = Number(el.dataset.start);
    videoEl.play().catch(() => {});
  },
  'rec.stamp': () => {
    const note = document.getElementById('rec-note');
    if (!note) return;
    note.value += `${note.value && !note.value.endsWith('\n') ? '\n' : ''}[${mmss(videoEl?.currentTime || 0)}] `;
    note.focus();
  },
  'rec.note': async (el) => {
    await api.put(`/recordings/${el.dataset.id}/note`, { body: { text: el.value } });
    toast('Note saved');
  },
});

export const routes = [studentList, trainerList, player('student'), player('professional')];
